import { useEffect, useCallback, useRef } from "react";
import { API, graphqlOperation, Hub } from "aws-amplify";
import {
  onUpdateLocationMini,
  onUpdateAisleMini,
  onCreateTrackCarMini,
  onUpdateTrackCarMini,
  onDeleteTrackCarMini,
} from "../graphql/trackCar/trackCar-mutations";
import { updateTracksWithCapacity } from "./trackListUtils";
import { logError } from "./appUtils";

const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS = 30000;
const RECONNECT_JITTER_RATIO = 0.2;
const RECONNECT_FAILURE_LOG_ATTEMPT = 3;

const getSubscriptionErrorMessage = (error) =>
  error?.errors?.[0]?.message ||
  error?.error?.errors?.[0]?.message ||
  error?.message ||
  "";

const isConnectionClosedError = (error) =>
  getSubscriptionErrorMessage(error) === "Connection closed";

const getReconnectDelayMs = (reconnectAttempt) => {
  const baseDelay = Math.min(
    RECONNECT_MAX_MS,
    RECONNECT_BASE_MS * 2 ** reconnectAttempt,
  );
  const jitter = Math.floor(baseDelay * RECONNECT_JITTER_RATIO * Math.random());
  return Math.min(RECONNECT_MAX_MS, baseDelay + jitter);
};

/**
 * Hook for managing GraphQL subscriptions for track management.
 * Handles real-time updates via 5 GraphQL subscriptions (Location, Aisle, TrackCar create/update/delete).
 * Includes staleness polling fallback and Hub reconnection logic.
 */
export const useTrackSubscriptions = ({
  reloadYardId,
  useSnapshot,
  subscriptionsDisabledRef,
  subscriptionsRef,
  processedCarsMapRef,
  lastSubscriptionEventRef,
  tracks,
  setTracks,
  addAction,
  getTrackCars,
  subscriptionsEpoch,
  setSubscriptionsEpoch,
}) => {
  const reconnectAttemptRef = useRef(0);
  const reconnectTimeoutRef = useRef(null);

  const markSubscriptionActivity = useCallback(() => {
    lastSubscriptionEventRef.current = Date.now();
    // Any live subscription payload means the socket/session is healthy; clear
    // reconnect backoff without relying on Hub `api` / `connected` (not always emitted).
    reconnectAttemptRef.current = 0;
  }, [lastSubscriptionEventRef]);

  const addActionRef = useRef(addAction);
  const getTrackCarsRef = useRef(getTrackCars);
  const setTracksRef = useRef(setTracks);
  const knownTrackIdsRef = useRef(new Set());

  useEffect(() => {
    addActionRef.current = addAction;
    getTrackCarsRef.current = getTrackCars;
    setTracksRef.current = setTracks;
    const nextKnownTrackIds = new Set();
    (tracks || []).forEach((track) => {
      const locationId = track?.locations?.items?.[0]?.id;
      if (locationId) nextKnownTrackIds.add(locationId);
    });
    knownTrackIdsRef.current = nextKnownTrackIds;
  }, [addAction, getTrackCars, setTracks, tracks]);

  useEffect(() => {
    if (!reloadYardId) return;
    if (subscriptionsDisabledRef.current) return;

    // Snapshot mode is historical EOD: skip all live subscriptions so the view does not drift.
    const runRealtimeSubscriptions = !useSnapshot;

    const findExistingTrackCar = (trackCarId, shipmentId) => {
      const carsByTrack = processedCarsMapRef.current || {};
      const allTrackIds = Object.keys(carsByTrack);
      for (let i = 0; i < allTrackIds.length; i++) {
        const currentTrackId = allTrackIds[i];
        const existingTrackCar = (carsByTrack[currentTrackId] || []).find(
          (trackCar) =>
            (trackCarId && trackCar.id === trackCarId) ||
            (shipmentId && trackCar.shipmentId === shipmentId),
        );
        if (existingTrackCar) {
          return { trackCar: existingTrackCar, trackId: currentTrackId };
        }
      }
      return { trackCar: null, trackId: null };
    };

    const isCanonicalTrackId = (trackId) =>
      trackId &&
      (knownTrackIdsRef.current.has(trackId) ||
        processedCarsMapRef.current[trackId] !== undefined);

    const shouldHandleTrackCarEvent = (trackCar) => {
      if (!trackCar) return false;

      const yardMatches =
        !trackCar.reloadYardId || trackCar.reloadYardId === reloadYardId;
      if (!yardMatches) return false;

      const { trackCar: existingTrackCar, trackId: existingTrackId } =
        findExistingTrackCar(trackCar.id, trackCar.shipmentId);
      if (existingTrackCar) return true;

      const trackId = trackCar.trackId;
      if (!isCanonicalTrackId(trackId)) {
        logError("TrackManagement:Sub:nonCanonicalTrackId", { trackCar }, null);
        getTrackCarsRef.current({ showSpinner: false, skipLoading: true });
        return false;
      }

      return (
        processedCarsMapRef.current[trackId] !== undefined ||
        knownTrackIdsRef.current.has(trackId) ||
        existingTrackId === trackId
      );
    };

    const isUnauthorizedError = (err) => {
      const msg = getSubscriptionErrorMessage(err);
      if (msg.includes("Not Authorized") || msg.includes("Unauthorized")) return true;
      try {
        const asString = JSON.stringify(err);
        return (
          asString.includes("Not Authorized") ||
          asString.includes("Unauthorized")
        );
      } catch (e) {
        return false;
      }
    };

    const logSubscriptionError = (label, err) => {
      if (!isConnectionClosedError(err)) {
        logError(label, err, null);
      }
    };

    const clearReconnectTimer = () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };

    const resetSubscriptions = () => {
      subscriptionsRef.current.forEach((sub) => {
        if (sub && typeof sub.unsubscribe === "function") sub.unsubscribe();
      });
      subscriptionsRef.current = [];
    };

    const disableSubscriptions = () => {
      subscriptionsDisabledRef.current = true;
      clearReconnectTimer();
      resetSubscriptions();
    };

    const removeSubscription = (sub) => {
      if (!sub) return;
      if (typeof sub.unsubscribe === "function") sub.unsubscribe();
      subscriptionsRef.current = subscriptionsRef.current.filter((s) => s !== sub);
    };

    const handleConnectionClosed = (label) => {
      if (subscriptionsDisabledRef.current || reconnectTimeoutRef.current) {
        return;
      }

      const reconnectAttempt = reconnectAttemptRef.current + 1;
      const reconnectDelayMs = getReconnectDelayMs(reconnectAttemptRef.current);
      reconnectAttemptRef.current = reconnectAttempt;

      resetSubscriptions();
      getTrackCarsRef.current({ showSpinner: false, skipLoading: true });

      if (reconnectAttempt >= RECONNECT_FAILURE_LOG_ATTEMPT) {
        logError(
          "TrackManagement:Sub:ReconnectFailed",
          {
            message: "Track subscription reconnect is still failing",
            sourceLabel: label,
            attempt: reconnectAttempt,
            nextDelayMs: reconnectDelayMs,
          },
          null,
        );
      }

      reconnectTimeoutRef.current = setTimeout(() => {
        reconnectTimeoutRef.current = null;
        setSubscriptionsEpoch((p) => p + 1);
      }, reconnectDelayMs);
    };

    const createSubscription = (label, query, next, options = {}) => {
      const { disableAllOnUnauthorized = true } = options;
      try {
        let sub = null;
        sub = API.graphql(graphqlOperation(query)).subscribe({
          next,
          error: (err) => {
            if (isUnauthorizedError(err)) {
              if (disableAllOnUnauthorized) {
                disableSubscriptions();
              } else {
                removeSubscription(sub);
              }
              return;
            }
            logSubscriptionError(label, err);
            if (isConnectionClosedError(err)) {
              handleConnectionClosed(label);
            }
          },
        });
        subscriptionsRef.current.push(sub);
        return sub;
      } catch (err) {
        if (isUnauthorizedError(err)) {
          if (disableAllOnUnauthorized) {
            disableSubscriptions();
          }
          return null;
        }
        logError(`${label}:init`, err, null);
        return null;
      }
    };

    const subscriptions = runRealtimeSubscriptions
      ? [
      createSubscription(
        "TrackManagement:Sub:Location",
        onUpdateLocationMini,
        ({ value }) => {
          const loc = value?.data?.onUpdateLocation;
          if (loc && (!loc.reloadYardId || loc.reloadYardId === reloadYardId)) {
            markSubscriptionActivity();
            setTracksRef.current((prev) => updateTracksWithCapacity(prev, [loc]));
          }
        },
        { disableAllOnUnauthorized: false }
      ),
      createSubscription(
        "TrackManagement:Sub:Aisle",
        onUpdateAisleMini,
        ({ value }) => {
          const aisle = value?.data?.onUpdateAisle;
          if (aisle && (!aisle.reloadYardId || aisle.reloadYardId === reloadYardId)) {
            markSubscriptionActivity();
            // Tracks in this flow are Aisle records (see schema Aisle.id, useTrackManagement).
            setTracksRef.current((prev) =>
              (prev || []).map((t) => (t.id === aisle.id ? { ...t, ...aisle } : t))
            );
          }
        },
        { disableAllOnUnauthorized: false }
      ),
      createSubscription(
        "TrackManagement:Sub:CreateTC",
        onCreateTrackCarMini,
        ({ value }) => {
          const tc = value?.data?.onCreateTrackCar;
          if (!tc?.id && !tc?.shipmentId) {
            logError("TrackManagement:Sub:CreateTC:missingCanonical", { tc }, null);
            getTrackCarsRef.current({ showSpinner: false, skipLoading: true });
            return;
          }
          if (shouldHandleTrackCarEvent(tc)) {
            markSubscriptionActivity();
            addActionRef.current({ type: "upsert", data: tc });
          }
        }
      ),
      createSubscription(
        "TrackManagement:Sub:UpdateTC",
        onUpdateTrackCarMini,
        ({ value }) => {
          const tc = value?.data?.onUpdateTrackCar;
          if (!tc?.id && !tc?.shipmentId) {
            logError("TrackManagement:Sub:UpdateTC:missingCanonical", { tc }, null);
            getTrackCarsRef.current({ showSpinner: false, skipLoading: true });
            return;
          }
          if (shouldHandleTrackCarEvent(tc)) {
            markSubscriptionActivity();
            if (tc?.active === "false") {
              addActionRef.current({ type: "delete", data: tc });
            } else {
              addActionRef.current({ type: "upsert", data: tc });
            }
          }
        }
      ),
      createSubscription(
        "TrackManagement:Sub:DeleteTC",
        onDeleteTrackCarMini,
        ({ value }) => {
          const tc = value?.data?.onDeleteTrackCar;
          if (!tc?.id && !tc?.shipmentId) {
            logError("TrackManagement:Sub:DeleteTC:missingCanonical", { tc }, null);
            getTrackCarsRef.current({ showSpinner: false, skipLoading: true });
            return;
          }
          if (shouldHandleTrackCarEvent(tc)) {
            markSubscriptionActivity();
            addActionRef.current({ type: "delete", data: tc });
          }
        }
      ),
    ]
      : [];

    return () => {
      clearReconnectTimer();
      subscriptions.forEach((s) => {
        if (s && typeof s.unsubscribe === "function") {
          s.unsubscribe();
        }
      });
      subscriptionsRef.current = [];
    };
  }, [
    reloadYardId,
    subscriptionsEpoch,
    markSubscriptionActivity,
    useSnapshot,
    subscriptionsDisabledRef,
    subscriptionsRef,
    processedCarsMapRef,
    setSubscriptionsEpoch,
  ]);

  useEffect(() => {
    if (!reloadYardId || useSnapshot) return;
    const CHECK_MS = 60000;
    const STALE_MS = 90000;
    const RESUBSCRIBE_MS = 180000;
    const id = setInterval(() => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      const elapsed = Date.now() - (lastSubscriptionEventRef.current || 0);
      if (elapsed < STALE_MS) return;
      lastSubscriptionEventRef.current = Date.now();
      getTrackCarsRef.current({ showSpinner: false, skipLoading: true });
      if (!subscriptionsDisabledRef.current && elapsed >= RESUBSCRIBE_MS) {
        setSubscriptionsEpoch((p) => p + 1);
      }
    }, CHECK_MS);
    return () => clearInterval(id);
  }, [reloadYardId, useSnapshot, lastSubscriptionEventRef, subscriptionsDisabledRef, setSubscriptionsEpoch]);

  useEffect(() => {
    if (useSnapshot) return;
    const unsub = Hub.listen("api", (data) => {
      if (data.payload?.event !== "connected") return;
      const hadPendingReconnect = Boolean(reconnectTimeoutRef.current);
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      reconnectAttemptRef.current = 0;
      if (subscriptionsDisabledRef.current) {
        subscriptionsDisabledRef.current = false;
        setSubscriptionsEpoch((p) => p + 1);
      } else if (hadPendingReconnect) {
        setSubscriptionsEpoch((p) => p + 1);
      }
      getTrackCarsRef.current({ showSpinner: false, skipLoading: true });
    });
    return () => unsub();
  }, [useSnapshot, subscriptionsDisabledRef, setSubscriptionsEpoch]);
};
